import { Box } from 'src/components/UI/Box';
import { BasicAbstractBackground } from 'src/components/BasicAbstractLayout';
import { Flex } from 'src/components/UI/Flex';
import Typography from 'src/components/Typography';
import ShareIcon from 'src/components/Icons/ShareIcon';
import { useCallback, useEffect, useState } from 'react';
import MevoInput from 'src/components/Input';
import { useFormik } from 'formik';
import { Yup } from 'src/helpers/Yup';
import useLoading from 'src/helpers/useLoading';
import { Button } from 'src/components/UI/Button';
import { usePrescriberContext } from 'src/providers/PrescriberProvider';
import AssistantCard from './AssistantCard';
import { useFeedbackContext } from 'src/providers/FeedbackProvider';
import { ReactComponent as EmptyIcon } from '../../assets/icons/mevo/ic_empty.svg';
import { Assistant, AssistantService } from 'src/services/AssistantService';
import { trackInviteAssistantClick, trackInviteAssistantPageView } from 'src/services/tracking/events';

export function InviteAssistant() {
  const [loading, runWithLoading] = useLoading();
  const { prescriberId } = usePrescriberContext();

  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const { openToast } = useFeedbackContext();

  const handleFetchAssistants = useCallback(async () => {
    try {
      const data = await AssistantService.getAssistants();

      setAssistants(data.prescriberAssistants);
    } catch (error) {
      openToast('Erro ao buscar assistentes, tente novamente mais tarde.', {
        type: 'error',
        duration: 2000,
      });
    }
  }, [openToast]);

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().email('Informe um email válido.').required('Informe um email válido (ex.: email@email.com).'),
    }),
    validateOnChange: false,
    async onSubmit(values) {
      if (!prescriberId) return;

      trackInviteAssistantClick(prescriberId || undefined);

      runWithLoading(async () => {
        try {
          await AssistantService.inviteAssistant({
            email: values.email,
            prescriberId: prescriberId,
            inviteUrl:
              process.env.NODE_ENV === 'development'
                ? `https://receita-staging.mevosaude.com.br/assistente/cadastro?email=${encodeURIComponent(values.email)}`
                : `${window.location.origin}/assistente/cadastro?email=${encodeURIComponent(values.email)}`,
          });

          handleFetchAssistants();

          openToast('Assistente convidado com sucesso!', {
            type: 'success',
            duration: 2000,
          });
        } catch (e: any) {
          openToast(e.response.data.message ?? 'Não foi possivel realizar o convite, tente novamente mais tarde.', {
            type: 'error',
            duration: 2000,
          });
        }
      });
    },
  });

  const handleRemoveAssistant = useCallback(
    async (assistantId: string) => {
      try {
        await AssistantService.removeAssistant(assistantId);
        handleFetchAssistants();
      } catch (error) {
        openToast('Erro ao remover assistente, tente novamente mais tarde.', {
          type: 'error',
          duration: 2000,
        });
      }
    },
    [handleFetchAssistants, openToast]
  );

  useEffect(() => {
    trackInviteAssistantPageView(prescriberId || undefined);

    const run = async () => {
      handleFetchAssistants();
    };

    run();
  }, [handleFetchAssistants, prescriberId]);

  return (
    <BasicAbstractBackground>
      <Flex flexDir={'column'} gap={'30px'} justifyContent={'center'} alignItems={'center'}>
        <Flex gap={'20px'} alignItems={'center'} m={'30px 0'} w={'100%'}>
          <ShareIcon variantColor="success.light" width={40} />

          <Box>
            <Typography model="h1" weight="bold">
              Convidar Assistente
            </Typography>
            <Typography model="body" size="large" weight="regular">
              Dê acesso para assistentes auxiliarem no cadastro de pacientes
            </Typography>
          </Box>
        </Flex>

        <Flex flexDir={{ base: 'column', md: 'row' }} gap={'30px'} w={'100%'}>
          <Box w={'100%'} p={'30px'} borderRadius={'20px'} bg={'#fff'} h={'100%'}>
            {assistants.length === 0 ? (
              <Flex justifyContent={'center'} alignItems={'center'} flexDirection={'column'} gap={'20px'}>
                <EmptyIcon />

                <Flex w={'200px'}>
                  <Typography model="body" size="normal" weight="regular" textAlign="center">
                    Você ainda não tem nenhum profissional atrelado ao seu cadastro, use o formulário ao lado para
                    convidar
                  </Typography>
                </Flex>
              </Flex>
            ) : (
              <Box w={'100%'}>
                <Typography>Assistentes cadastrados</Typography>

                <Flex flexDir={'column'} gap={'20px'} mt={'20px'}>
                  {assistants.map((assistant) => (
                    <AssistantCard data={assistant} onRemove={() => handleRemoveAssistant(assistant.assistant._id)} />
                  ))}
                </Flex>
              </Box>
            )}
          </Box>

          <Flex w={'100%'} p={'30px'} borderRadius={'20px'} bg={'#fff'} gap={'30px'} flexDir={'column'}>
            <Typography>Convidar novo profissional</Typography>

            <form onSubmit={formik.handleSubmit}>
              <MevoInput
                id="invite-assistant:email"
                labelText="Email"
                h={'45px'}
                placeholderText={'Insira o email'}
                name={'email'}
                type={'text'}
                onChange={formik.handleChange}
                value={formik.values.email}
                errorText={formik.errors.email}
                isInvalid={!!formik.errors.email}
              />

              <Flex justifyContent={'flex-end'}>
                <Button isLoading={loading} height="60px" w={'110px'} type="submit" mt="16px">
                  Convidar
                </Button>
              </Flex>
            </form>
          </Flex>
        </Flex>
      </Flex>
    </BasicAbstractBackground>
  );
}
